import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { MagnifyingGlass, SignOut, ArrowsLeftRight } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import NotificationsBell from '@/components/NotificationsBell';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import Sidebar from './shell/Sidebar';
import { COLLECTIONS_NAV, LEGAL_NAV, DEPT_BRAND } from './shell/navConfig';

export default function AppShell({ department = 'collections' }) {
  const { user, logout, setCurrentDepartment } = useAuth();
  const nav2 = useNavigate();

  const isAdmin = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'legal_manager';
  const nav = department === 'legal' ? LEGAL_NAV : COLLECTIONS_NAV;
  const brand = DEPT_BRAND[department];
  const userDepts = user?.departments || (user?.role === 'admin' ? ['collections', 'legal'] : ['collections']);

  const switchDepartment = () => {
    setCurrentDepartment(null);
    nav2('/select-department');
  };

  return (
    <div className="min-h-screen flex bg-[#F8F9FA]">
      <Sidebar
        department={department}
        brand={brand}
        nav={nav}
        isAdmin={isAdmin}
        canSwitch={userDepts.length > 1}
        onSwitch={switchDepartment}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 sticky top-0 z-30 backdrop-blur-xl bg-white/90" data-testid="topbar">
          <div className="flex-1 max-w-xl">
            <div className="relative">
              <MagnifyingGlass className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                data-testid="global-search-input"
                placeholder={department === 'legal' ? "بحث في المطالبات، القضايا..." : "بحث في الفواتير، المستأجرين، العقود..."}
                className="pr-10 h-10 bg-slate-50 border-slate-200"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <NotificationsBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 h-10 px-3" data-testid="user-menu">
                  <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold">
                    {user?.name?.charAt(0) || 'م'}
                  </div>
                  <div className="text-start leading-tight hidden md:block">
                    <div className="text-sm font-medium text-slate-900">{user?.name}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">{user?.role}</div>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {userDepts.length > 1 && (
                  <DropdownMenuItem onClick={switchDepartment} data-testid="menu-switch-dept">
                    <ArrowsLeftRight className="ms-2" size={16} />
                    تبديل الإدارة
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={logout} data-testid="logout-button">
                  <SignOut className="ms-2" size={16} />
                  تسجيل الخروج
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
